// useBddOptions — the people who can be recorded as having sold a building.
//
// ⛔ THIS IS NOT A NEW PICKER. It is the same read the onboarding card's "BDD"
// field already does: v_employee_picker filtered on is_bdd_eligible. The card
// and the building must offer the same list or the same fact gets two answers.
// PICKERS EXCLUDE, RESOLVERS RESOLVE (TCSD-0129) — a building whose recorded
// seller is not in this list still DISPLAYS that person's name, because the
// name is resolved from the eid, not looked up in the picker.
//
// v_employee_picker runs with owner rights and tests the CALLER (is_app_employee).
// That is the normal, correct shape for a picker here and is declared in
// public.view_security_exceptions — an empty list read from outside the app is
// the gate doing its job, not a defect (PLAYBOOK §16).
//
// ⛔ An empty list is NOT an empty answer (PLAYBOOK §15.3). A refused or failed
// read returns `error` and the caller says the list could not be loaded, rather
// than rendering a dropdown that looks like the company employs no sellers.
// ============================================================================

import { useEffect, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { errorMessage } from '../../../shared/errors';

export interface BddOption {
  employee_id: string;
  name: string;
}

export function useBddOptions(enabled = true): {
  bdds: BddOption[];
  loading: boolean;
  error: string | null;
} {
  const [bdds, setBdds] = useState<BddOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const { data, error: e } = await supabase
        .from('v_employee_picker')
        .select('employee_id, employee_name_first, employee_name_last')
        .eq('is_bdd_eligible', true)
        .order('employee_name_first');
      if (cancelled) return;
      setLoading(false);
      if (e) {
        setError(errorMessage(e, 'The list of business developers could not be loaded.'));
        return;
      }
      setError(null);
      setBdds(
        (data ?? []).map((r) => ({
          employee_id: r.employee_id as string,
          name:
            [r.employee_name_first, r.employee_name_last].filter(Boolean).join(' ').trim() ||
            (r.employee_id as string),
        })),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { bdds, loading, error };
}
